import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Container, Card, Row, Col, Form, Button, Badge, Table, Spinner, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api.js';
import Menu from '../../comum/Menu/menu.jsx';
import Footer from '../../comum/Footer/index.jsx';
import Foto from "../Components/Foto";
import './dashboard.css';

const fotoCache = new Map();

const getPrimeiroDiaMesAnterior = () => {
    const hoje = new Date();
    const dataMesAnterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
    const ano = dataMesAnterior.getFullYear();
    const mes = String(dataMesAnterior.getMonth() + 1).padStart(2, '0');
    return `${ano}-${mes}-01`;
};

const carinhasMap = {
    1: { emoji: '😢', rotulo: 'Péssimo', cor: 'danger' },
    2: { emoji: '😟', rotulo: 'Ruim', cor: 'warning' },
    3: { emoji: '😐', rotulo: 'Neutro', cor: 'secondary' },
    4: { emoji: '🙂', rotulo: 'Bom', cor: 'info' },
    5: { emoji: '😁', rotulo: 'Excelente', cor: 'success' }
};

// Componente isolado para renderizar avatar com foto em background sem afetar o estado pai
const FotoFuncionarioAvatar = React.memo(({ chapa, nome, info01, info02, onSelectFoto, styleCustom }) => {
    const [fotoSrc, setFotoSrc] = useState(null);

    useEffect(() => {
        if (!chapa) return;
        let isMounted = true;
        const hoje = new Date().toISOString().slice(0, 10);
        const chave = `func_${chapa}_${hoje}`;

        if (fotoCache.has(chave)) {
            setFotoSrc(fotoCache.get(chave));
            return;
        }

        api.get(`/usuario/foto/${chapa}`)
            .then(res => {
                if (isMounted && res?.data?.foto) {
                    fotoCache.set(chave, res.data.foto);
                    setFotoSrc(res.data.foto);
                }
            })
            .catch(() => { });

        return () => { isMounted = false; };
    }, [chapa]);

    const handleClick = (e) => {
        if (e) e.stopPropagation();
        if (fotoSrc && onSelectFoto) {
            onSelectFoto({
                foto: `data:image/jpeg;base64,${fotoSrc}`,
                nome: nome || "",
                info01: info01 || `Matrícula: ${chapa}`,
                info02: info02 || ""
            });
        }
    };

    return (
        <div
            className="foto-avatar-list"
            onClick={handleClick}
            style={styleCustom || {}}
        >
            {fotoSrc ? (
                <img
                    src={`data:image/jpeg;base64,${fotoSrc}`}
                    alt="foto"
                    style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                />
            ) : (
                <span>👤</span>
            )}
        </div>
    );
});

const CalendarioReal = ({ dados, sentimentoEmEvidencia, dataInicio, dataFim }) => {
    const criarDataLocal = (strData) => {
        if (!strData) return new Date();
        const partes = String(strData).split('T')[0].split(' ')[0].split('-');
        return partes.length === 3 ? new Date(parseInt(partes[0], 10), parseInt(partes[1], 10) - 1, parseInt(partes[2], 10)) : new Date(strData);
    };

    const mapaSentimentos = useMemo(() => {
        const mapa = {};
        (dados || []).forEach(item => {
            const rawData = item.data_iso || item.DATA_ISO || item.data || item.DATA || item.data_registro || item.DATA_REGISTRO || '';
            let dataIso = '';
            
            if (rawData) {
                if (rawData.includes('T')) {
                    dataIso = rawData.split('T')[0];
                } else if (rawData.includes(' ')) {
                    dataIso = rawData.split(' ')[0];
                } else if (rawData.includes('/')) {
                    const parts = rawData.split('/');
                    if (parts.length === 3) dataIso = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
                } else {
                    dataIso = rawData;
                }
            }
            
            if (dataIso) {
                mapa[dataIso.trim()] = item;
            }
        });
        return mapa;
    }, [dados]);

    const mesesParaExibir = useMemo(() => {
        const dtIni = criarDataLocal(dataInicio);
        const dtFim = criarDataLocal(dataFim);
        const lista = [];

        let anoAtual = dtIni.getFullYear();
        let mesAtual = dtIni.getMonth();

        const anoFim = dtFim.getFullYear();
        const mesFim = dtFim.getMonth();

        while (anoAtual < anoFim || (anoAtual === anoFim && mesAtual <= mesFim)) {
            lista.push(new Date(anoAtual, mesAtual, 1));
            mesAtual++;
            if (mesAtual > 11) {
                mesAtual = 0;
                anoAtual++;
            }
        }
        return lista;
    }, [dataInicio, dataFim]);

    const diasSemana = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

    return (
        <div className="d-flex flex-column gap-3">
            {mesesParaExibir.map((dtMes) => {
                const ano = dtMes.getFullYear();
                const mes = dtMes.getMonth();
                const primeiroDiaMes = new Date(ano, mes, 1);
                const ultimoDiaMes = new Date(ano, mes + 1, 0);
                const diaSemanaInicial = primeiroDiaMes.getDay();
                const totalDias = ultimoDiaMes.getDate();

                const celulas = [];
                for (let i = 0; i < diaSemanaInicial; i++) {
                    celulas.push({ tipo: 'vazio', key: `vazio-${i}` });
                }

                for (let dia = 1; dia <= totalDias; dia++) {
                    const mesFmt = String(mes + 1).padStart(2, '0');
                    const diaFmt = String(dia).padStart(2, '0');
                    const chaveData = `${ano}-${mesFmt}-${diaFmt}`;
                    celulas.push({ tipo: 'dia', key: chaveData, dia, chaveData, registro: mapaSentimentos[chaveData] });
                }

                const nomeMes = dtMes.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });

                return (
                    <div key={`${ano}-${mes}`} className="border rounded p-2 p-md-3 bg-light shadow-sm">
                        <h6 className="text-center fw-bold text-uppercase text-arbor mb-2">{nomeMes}</h6>
                        <div className="d-grid text-center fw-bold text-muted mb-1 grade-dias-semana">
                            {diasSemana.map((d, i) => (
                                <div key={i} className="py-1 bg-white border rounded small">{d}</div>
                            ))}
                        </div>
                        <div className="d-grid text-center gap-1 grade-calendario-dias">
                            {celulas.map((cel) => {
                                if (cel.tipo === 'vazio') return <div key={cel.key} className="p-1 border-0"></div>;
                                const reg = cel.registro;
                                const sentimentoNum = reg ? (reg.sentimento ?? reg.SENTIMENTO ?? reg.valor ?? reg.VALOR) : null;
                                const ehEvidencia = sentimentoEmEvidencia && String(sentimentoNum) === String(sentimentoEmEvidencia);

                                return (
                                    <div
                                        key={cel.key}
                                        className={`celula-dia p-1 border rounded bg-white d-flex flex-column align-items-center justify-content-between ${ehEvidencia ? 'border-2 border-warning' : ''}`}
                                        style={{ backgroundColor: ehEvidencia ? '#fffde7' : '#ffffff' }}
                                    >
                                        <span className="num-dia fw-bold text-muted">{cel.dia}</span>
                                        <div className="d-flex align-items-center justify-content-center flex-grow-1">
                                            {sentimentoNum ? (
                                                <div className="emoji-calendario">{carinhasMap[sentimentoNum]?.emoji}</div>
                                            ) : (
                                                <span className="text-light fs-6">•</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

function HumorEquipe() {
    const navigate = useNavigate();
    const hojeISO = new Date().toISOString().slice(0, 10);

    const [filtroDataIniHumor, setFiltroDataIniHumor] = useState(getPrimeiroDiaMesAnterior());
    const [filtroDataFimHumor, setFiltroDataFimHumor] = useState(hojeISO);
    const [lojaSelecionada, setLojaSelecionada] = useState('0');
    const [filtroMatriculaHumor, setFiltroMatriculaHumor] = useState('');
    const [sentimentoSelecionado, setSentimentoSelecionado] = useState('0');

    const [loading, setLoading] = useState(false);
    const [resumoHumores, setResumoHumores] = useState({ total: 0, cont1: 0, cont2: 0, cont3: 0, cont4: 0, cont5: 0, media: 0 });
    const [detalhesHumores, setDetalhesHumores] = useState([]);

    const [temPermissao, setTemPermissao] = useState(null);
    const [erroPermissao, setErroPermissao] = useState(false);
    const [lojasPermitidas, setLojasPermitidas] = useState([]);

    const [showModalCalendario, setShowModalCalendario] = useState(false);
    const [funcCalendario, setFuncCalendario] = useState(null);
    const [dadosCalendario, setDadosCalendario] = useState([]);
    const [loadingCalendario, setLoadingCalendario] = useState(false);
    const [fotoSelecionada, setFotoSelecionada] = useState(null);

    const [ordemCampo, setOrdemCampo] = useState('nome');
    const [ordemDirecao, setOrdemDirecao] = useState('asc');

    useEffect(() => {
        const permitePonto = localStorage.getItem("permitePonto");
        if (permitePonto === 'N') {
            setTemPermissao(false);
            setErroPermissao(true);
            return;
        }

        const lojaStorage = localStorage.getItem("loja") || '0';
        const lojas = JSON.parse(localStorage.getItem("lojasPermitidas") || "[]");

        setLojasPermitidas(lojas);
        setLojaSelecionada(lojaStorage);
        setTemPermissao(permitePonto === "S" || lojas.length > 0);
    }, []);

    const loadHumores = useCallback(async () => {
        if (!temPermissao) return;
        setLoading(true);
        try {
            const filialParam = lojaSelecionada === '0' ? (lojasPermitidas.join(',') || '999') : lojaSelecionada;
            const resResumo = await api.get(`/rh/sentimentos/resumo?dataini=${filtroDataIniHumor}&datafim=${filtroDataFimHumor}&filial=${filialParam}`);
            setResumoHumores(resResumo.data || { total: 0, cont1: 0, cont2: 0, cont3: 0, cont4: 0, cont5: 0, media: 0 });

            const resDetalhes = await api.get(`/rh/sentimentos/detalhes?dataini=${filtroDataIniHumor}&datafim=${filtroDataFimHumor}&filial=${filialParam}&sentimento=${sentimentoSelecionado}&matricula=${filtroMatriculaHumor}`);
            const lista = resDetalhes.data || [];
            setDetalhesHumores(lista);
        } catch (err) {
            console.error('Erro ao carregar humores:', err);
        } finally {
            setLoading(false);
        }
    }, [temPermissao, lojaSelecionada, lojasPermitidas, filtroDataIniHumor, filtroDataFimHumor, sentimentoSelecionado, filtroMatriculaHumor]);

    useEffect(() => {
        if (temPermissao) {
            loadHumores();
        }
    }, [loadHumores, temPermissao]);

    const handleOrdenar = (campo) => {
        if (ordemCampo === campo) {
            setOrdemDirecao(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setOrdemCampo(campo);
            setOrdemDirecao('asc');
        }
    };

    const dadosOrdenados = useMemo(() => {
        return [...detalhesHumores].sort((a, b) => {
            let valA = a[ordemCampo] ?? a[ordemCampo.toUpperCase()] ?? '';
            let valB = b[ordemCampo] ?? b[ordemCampo.toUpperCase()] ?? '';

            if (ordemCampo === 'codfilial' || ordemCampo === 'chapa' || ordemCampo === 'total_respostas' || ordemCampo === 'media_func') {
                valA = Number(valA) || 0;
                valB = Number(valB) || 0;
            } else {
                valA = String(valA).toLowerCase();
                valB = String(valB).toLowerCase();
            }

            if (valA < valB) return ordemDirecao === 'asc' ? -1 : 1;
            if (valA > valB) return ordemDirecao === 'asc' ? 1 : -1;
            return 0;
        });
    }, [detalhesHumores, ordemCampo, ordemDirecao]);

    const abrirCalendarioFuncionario = async (item) => {
        setFuncCalendario(item);
        setShowModalCalendario(true);
        setLoadingCalendario(true);
        try {
            const chapa = item.chapa || item.CHAPA;
            const res = await api.get(`/rh/sentimentos/calendario/${chapa}?dataini=${filtroDataIniHumor}&datafim=${filtroDataFimHumor}`);
            setDadosCalendario(res.data || []);
        } catch (err) {
            setDadosCalendario([]);
        } finally {
            setLoadingCalendario(false);
        }
    };

    const renderSeta = (campo) => {
        if (ordemCampo !== campo) return ' ↕';
        return ordemDirecao === 'asc' ? ' ▲' : ' ▼';
    };

    return (
        <div className="layout-rh d-flex flex-column min-vh-100">
            <Menu modulo="ARBOR" />
            
            <Container className="flex-grow-1 container-humor-page pb-4" style={{ marginTop: '75px' }}>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
                    <div>
                        <h4 className="fw-bold text-dark mb-0">Pesquisa de Clima & Sentimentos</h4>
                        <p className="text-muted small mb-0">Acompanhamento da equipe nas lojas permitidas</p>
                    </div>
                    <div className="d-flex align-items-center gap-2 bg-white px-3 py-2 rounded border shadow-sm align-self-start align-self-md-auto">
                        <span className="fw-bold small text-muted">Média do Período:</span>
                        <span className="fs-4 fw-bold text-arbor">{resumoHumores.media || '0.0'}</span>
                        <span className="fs-3">{carinhasMap[Math.round(resumoHumores.media || 3)]?.emoji}</span>
                    </div>
                </div>

                {/* FILTROS */}
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body className="p-3">
                        <Row className="g-2 align-items-end">
                            <Col xs={6} md={3}>
                                <Form.Label className="small fw-bold mb-1">Data Início</Form.Label>
                                <Form.Control size="sm" type="date" value={filtroDataIniHumor} onChange={e => setFiltroDataIniHumor(e.target.value)} />
                            </Col>
                            <Col xs={6} md={3}>
                                <Form.Label className="small fw-bold mb-1">Data Fim</Form.Label>
                                <Form.Control size="sm" type="date" value={filtroDataFimHumor} onChange={e => setFiltroDataFimHumor(e.target.value)} />
                            </Col>
                            <Col xs={6} md={2}>
                                <Form.Label className="small fw-bold mb-1">Loja</Form.Label>
                                <Form.Select size="sm" value={lojaSelecionada} onChange={e => setLojaSelecionada(e.target.value)}>
                                    <option value="0">Todas Permitidas</option>
                                    {lojasPermitidas.map(lj => (
                                        <option key={lj} value={lj}>{String(lj).padStart(2, '0')}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col xs={6} md={2}>
                                <Form.Label className="small fw-bold mb-1">Matrícula</Form.Label>
                                <Form.Control size="sm" type="text" value={filtroMatriculaHumor} onChange={e => setFiltroMatriculaHumor(e.target.value)} placeholder="Opcional" />
                            </Col>
                            <Col xs={12} md={2}>
                                <Button size="sm" className="w-100 fw-bold btn-arbor" onClick={loadHumores} disabled={loading}>
                                    {loading ? <Spinner animation="border" size="sm" /> : "Consultar"}
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* CARDS CARINHAS */}
                <div className="grid-humores-cards mb-4">
                    {[1, 2, 3, 4, 5].map((st) => {
                        const count = resumoHumores[`cont${st}`] || 0;
                        const pct = resumoHumores.total > 0 ? ((count / resumoHumores.total) * 100).toFixed(1) : "0.0";
                        const isSelected = sentimentoSelecionado === String(st);

                        return (
                            <Card
                                key={st}
                                className={`item-carinha text-center transition-all ${isSelected ? 'border-arbor shadow' : 'border-light shadow-sm'}`}
                                style={{ cursor: 'pointer', backgroundColor: isSelected ? '#fdf2f2' : '#fff' }}
                                onClick={() => setSentimentoSelecionado(isSelected ? '0' : String(st))}
                            >
                                <Card.Body className="p-2">
                                    <div className="emoji-header">{carinhasMap[st].emoji}</div>
                                    <div className="fw-bold small text-muted mb-1">{carinhasMap[st].rotulo}</div>
                                    <Badge bg={carinhasMap[st].cor} className="w-100 fs-7">
                                        {pct}% ({count})
                                    </Badge>
                                </Card.Body>
                            </Card>
                        );
                    })}
                </div>

                {/* LISTAGEM DOS COLABORADORES */}
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white fw-bold d-flex justify-content-between align-items-center py-3">
                        <span>Lista de Colaboradores ({dadosOrdenados.length})</span>
                        {sentimentoSelecionado !== '0' && (
                            <Button size="sm" variant="outline-secondary" onClick={() => setSentimentoSelecionado('0')}>
                                Limpar Filtro de Carinha
                            </Button>
                        )}
                    </Card.Header>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" className="text-arbor" />
                            <div className="text-muted mt-2 fw-bold small">A carregar colaboradores...</div>
                        </div>
                    ) : (
                        <>
                            {/* VISÃO DESKTOP */}
                            <div className="d-none d-md-block">
                                <Table hover responsive className="mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th style={{ width: '60px' }}>Foto</th>
                                            <th className="sortable" style={{ width: '70px' }} onClick={() => handleOrdenar('codfilial')}>
                                                Loja{renderSeta('codfilial')}
                                            </th>
                                            <th className="sortable" style={{ width: '100px' }} onClick={() => handleOrdenar('chapa')}>
                                                Matrícula{renderSeta('chapa')}
                                            </th>
                                            <th className="sortable" onClick={() => handleOrdenar('nome')}>
                                                Funcionário{renderSeta('nome')}
                                            </th>
                                            <th>Função / Seção</th>
                                            <th className="text-center sortable" style={{ width: '110px' }} onClick={() => handleOrdenar('total_respostas')}>
                                                Respostas{renderSeta('total_respostas')}
                                            </th>
                                            <th className="text-center" style={{ width: '100px' }}>Humor Média</th>
                                            <th className="text-center sortable" style={{ width: '90px' }} onClick={() => handleOrdenar('media_func')}>
                                                Média{renderSeta('media_func')}
                                            </th>
                                            <th className="text-center" style={{ width: '140px' }}>Histórico</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {dadosOrdenados.map((item, idx) => {
                                            const chapa = item.chapa || item.CHAPA;
                                            const mediaFunc = item.media_func || item.MEDIA_FUNC || 0;
                                            const numLoja = String(item.codfilial || item.CODFILIAL || '').padStart(2, '0');

                                            return (
                                                <tr key={idx}>
                                                    <td>
                                                        <FotoFuncionarioAvatar
                                                            chapa={chapa}
                                                            nome={item.nome || item.NOME || ""}
                                                            info01={`Matrícula: ${chapa}`}
                                                            info02={`Loja ${numLoja}`}
                                                            onSelectFoto={setFotoSelecionada}
                                                        />
                                                    </td>
                                                    <td className="fw-bold">{numLoja}</td>
                                                    <td className="fw-bold">{chapa}</td>
                                                    <td className="fw-bold text-uppercase">{item.nome || item.NOME}</td>
                                                    <td className="small text-muted">
                                                        <div>{item.funcao || item.FUNCAO}</div>
                                                        <small className="text-secondary">{item.secao || item.SECAO}</small>
                                                    </td>
                                                    <td className="text-center">
                                                        <Badge bg="light" text="dark" className="border px-2 py-1">
                                                            {item.total_respostas || item.TOTAL_RESPOSTAS || 1} registro(s)
                                                        </Badge>
                                                    </td>
                                                    <td className="fs-4 text-center">{carinhasMap[Math.round(mediaFunc)]?.emoji || '😐'}</td>
                                                    <td className="text-center fw-bold text-arbor fs-6">{Number(mediaFunc).toFixed(1)}</td>
                                                    <td className="text-center">
                                                        <Button size="sm" variant="outline-danger" className="fw-bold" onClick={() => abrirCalendarioFuncionario(item)}>
                                                            📅 Ver Histórico
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>

                            {/* VISÃO MOBILE */}
                            <div className="d-block d-md-none p-2">
                                {dadosOrdenados.map((item, idx) => {
                                    const chapa = item.chapa || item.CHAPA;
                                    const mediaFunc = item.media_func || item.MEDIA_FUNC || 0;
                                    const numLoja = String(item.codfilial || item.CODFILIAL || '').padStart(2, '0');

                                    return (
                                        <Card key={idx} className="mb-2 border border-light shadow-sm">
                                            <Card.Body className="p-3">
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <FotoFuncionarioAvatar
                                                            chapa={chapa}
                                                            nome={item.nome || item.NOME || ""}
                                                            info01={`Matrícula: ${chapa}`}
                                                            info02={`Loja ${numLoja}`}
                                                            onSelectFoto={setFotoSelecionada}
                                                        />
                                                        <div>
                                                            <Badge bg="secondary" className="mb-1">{numLoja}</Badge>
                                                            <h6 className="fw-bold mb-0 text-uppercase" style={{ fontSize: '0.85rem' }}>{item.nome || item.NOME}</h6>
                                                            <small className="text-muted d-block">Matrícula: {chapa}</small>
                                                        </div>
                                                    </div>
                                                    <div className="text-end">
                                                        <span className="fs-2 d-block leading-none">{carinhasMap[Math.round(mediaFunc)]?.emoji || '😐'}</span>
                                                        <span className="fw-bold text-arbor small">Média {Number(mediaFunc).toFixed(1)}</span>
                                                    </div>
                                                </div>
                                                <div className="small text-muted mb-2 border-top pt-2">
                                                    <div><strong>Função:</strong> {item.funcao || item.FUNCAO}</div>
                                                    <div><strong>Seção:</strong> {item.secao || item.SECAO}</div>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                                                    <span className="x-small text-muted">{item.total_respostas || item.TOTAL_RESPOSTAS || 1} resposta(s)</span>
                                                    <Button size="sm" variant="outline-danger" className="fw-bold py-1 px-2" onClick={() => abrirCalendarioFuncionario(item)}>
                                                        📅 Histórico
                                                    </Button>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    );
                                })}
                            </div>

                            {dadosOrdenados.length === 0 && (
                                <div className="text-center py-4 text-muted">
                                    Nenhum colaborador encontrado para este filtro no período.
                                </div>
                            )}
                        </>
                    )}
                </Card>
            </Container>

            {/* MODAL DE CALENDÁRIO COM FOTO AMPLIÁVEL */}
            <Modal show={showModalCalendario} onHide={() => setShowModalCalendario(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-arbor text-white">
                    <Modal.Title className="fs-6 fw-bold">
                        📅 Histórico de Humor do Colaborador
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-3">
                    {funcCalendario && (
                        <Card className="border-0 bg-light shadow-sm mb-3">
                            <Card.Body className="p-3">
                                <div className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3">
                                    <div className="d-flex align-items-center gap-3">
                                        <FotoFuncionarioAvatar
                                            chapa={funcCalendario.chapa || funcCalendario.CHAPA}
                                            nome={funcCalendario.nome || funcCalendario.NOME || ""}
                                            info01={`Matrícula: ${funcCalendario.chapa || funcCalendario.CHAPA}`}
                                            info02={`Loja ${String(funcCalendario.codfilial || funcCalendario.CODFILIAL || '').padStart(2, '0')}`}
                                            onSelectFoto={setFotoSelecionada}
                                            styleCustom={{ width: 64, height: 64, borderRadius: '50%', background: '#fff', border: '2px solid #a81a1a', flexShrink: 0 }}
                                        />
                                        <div>
                                            <div className="d-flex align-items-center gap-2">
                                                <Badge bg="dark">Loja {String(funcCalendario.codfilial || funcCalendario.CODFILIAL || '').padStart(2, '0')}</Badge>
                                                <span className="text-muted small">Matrícula: <strong>{funcCalendario.chapa || funcCalendario.CHAPA}</strong></span>
                                            </div>
                                            <h5 className="fw-bold mb-0 text-uppercase mt-1 text-dark" style={{ fontSize: '1.05rem' }}>
                                                {funcCalendario.nome || funcCalendario.NOME}
                                            </h5>
                                            <div className="small text-secondary fw-semibold">
                                                {funcCalendario.funcao || funcCalendario.FUNCAO || '-'}
                                                {(funcCalendario.secao || funcCalendario.SECAO) ? ` (${funcCalendario.secao || funcCalendario.SECAO})` : ''}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-center text-sm-end bg-white p-2 px-3 rounded border">
                                        <span className="x-small text-muted d-block fw-bold">MÉDIA NO PERÍODO</span>
                                        <div className="d-flex align-items-center gap-2 justify-content-center justify-content-sm-end">
                                            <span className="fs-3">{carinhasMap[Math.round(funcCalendario.media_func || funcCalendario.MEDIA_FUNC || 3)]?.emoji}</span>
                                            <span className="fs-4 fw-bold text-arbor">
                                                {Number(funcCalendario.media_func || funcCalendario.MEDIA_FUNC || 0).toFixed(1)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {loadingCalendario ? (
                        <div className="text-center py-4"><Spinner animation="border" className="text-arbor" /></div>
                    ) : (
                        <CalendarioReal
                            dados={dadosCalendario}
                            sentimentoEmEvidencia={sentimentoSelecionado !== '0' ? sentimentoSelecionado : null}
                            dataInicio={filtroDataIniHumor}
                            dataFim={filtroDataFimHumor}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={() => setShowModalCalendario(false)}>Fechar</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={erroPermissao} onHide={() => navigate('/arbor/dadosFunc')} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Acesso Negado</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Você não possui permissão para acessar o módulo de <strong>Pesquisa de Clima & Sentimentos</strong>.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => navigate('/arbor/dadosFunc')}>
                        Voltar ao início
                    </Button>
                </Modal.Footer>
            </Modal>

            <Footer />

            <Foto
                show={!!fotoSelecionada}
                onClose={() => setFotoSelecionada(null)}
                foto={fotoSelecionada?.foto}
                nome={fotoSelecionada?.nome}
                info01={fotoSelecionada?.info01}
                info02={fotoSelecionada?.info02}
            />
        </div>
    );
}

export default HumorEquipe;